/**
 * Splits flight segments into departure and return flight legs.
 *
 * This function analyzes a list of flight segments and separates them into
 * outbound (departure) and inbound (return) flights for round-trip journeys.
 *
 * **Detection strategies:**
 *
 * 1. **Primary: stopTime logic** - A segment without `stopTime` (after the first segment)
 *    indicates the start of a new flight leg (return flight).
 *
 * 2. **Fallback for 2-segment flights** - For simple round trips with exactly 2 segments,
 *    detects when the destination matches the original origin (e.g., BSR → BGW → BSR).
 *    This handles cases where airlines incorrectly include `stopTime` on the return segment.
 *
 * 3. **Fallback for multi-segment round trips** - Analyzes the travel chain to find the
 *    "turnaround point" - a city that appears exactly once in the journey. For example,
 *    in BSR → DXB → BGW → DXB → BSR, BGW appears once and marks where the return journey begins.
 *    This only applies when there's exactly one non-repeating city.
 *
 * The function also clears incorrect `stopTime` values on segments detected as return flight
 * starts via fallback logic.
 *
 * @template T - Segment type with origin, destination, and optional stopTime
 * @param segments - Array of flight segments to split
 * @returns Object containing `departureFlight` and `returnFlight` segment arrays
 */
export function splitSegmentsToFlights<
  T extends {
    origin: { code: string };
    destination: { code: string };
    stopTime?: string | null;
  },
>(segments: T[]) {
  const isTwoSegmentFlight = segments.length === 2;

  // Build frequency map of cities in the travel chain (for multi-segment round trips)
  const cityFrequency = new Map<string, number>();

  // Add first segment's origin
  if (segments.length > 0) {
    const firstCity = segments[0].origin.code;
    cityFrequency.set(firstCity, (cityFrequency.get(firstCity) || 0) + 1);
  }

  // Add each segment's destination to build the travel chain
  segments.forEach((segment) => {
    const city = segment.destination.code;
    cityFrequency.set(city, (cityFrequency.get(city) || 0) + 1);
  });

  // Find cities that appear exactly once (turnaround point)
  const nonRepeatingCities = Array.from(cityFrequency.entries())
    .filter(([, count]) => count === 1)
    .map(([city]) => city);

  // Use turnaround point logic only if there's exactly one non-repeating city
  const turnaroundCity =
    nonRepeatingCities.length === 1 ? nonRepeatingCities[0] : null;

  return segments.reduce(
    (acc, segment, index) => {
      // For 2-segment flights, check if returning to origin to handle incorrect stopTime
      const isReturningToOrigin =
        isTwoSegmentFlight &&
        acc.departureFlight.length > 0 &&
        segment.destination.code === acc.departureFlight[0].origin.code;

      // Check if this segment starts from the turnaround city (fallback for multi-segment round trips)
      const isStartingFromTurnaround =
        turnaroundCity && segment.origin.code === turnaroundCity;

      // Start treating segments as return flight when:
      // 1. We haven't started collecting return flights yet, AND
      // 2. Either:
      //    - (for 2-segment flights) the segment is returning to origin, OR
      //    - it's not the first segment and has no stopTime, OR
      //    - (fallback) this segment starts from the turnaround city
      if (
        acc.returnFlight.length === 0 &&
        (isReturningToOrigin ||
          (index > 0 && !segment.stopTime) ||
          isStartingFromTurnaround)
      ) {
        const seg = { ...segment };
        if (
          (isReturningToOrigin || isStartingFromTurnaround) &&
          segment.stopTime
        ) {
          seg.stopTime = null;
        }
        acc.returnFlight.push(seg);
      } else if (acc.returnFlight.length > 0) {
        // Already collecting return flight segments
        acc.returnFlight.push(segment);
      } else {
        // Still on departure flight
        acc.departureFlight.push(segment);
      }

      return acc;
    },
    { departureFlight: [], returnFlight: [] } as {
      departureFlight: T[];
      returnFlight: T[];
    },
  );
}
